/**
 * PRD.270 — Walk-in services → invoice import: PowerSync query.
 *
 * Pure eligibility / mapping / ordering logic lives in `walk-in-billing-logic.ts`
 * (dependency-free, unit-tested); this file adds the reactive query and re-exports
 * the logic for a single import surface.
 */

import type { QuerySpec } from '@helios/powersync-client-core';
import {
  WALK_IN_SOURCE_TYPE,
  WALK_IN_QUEUE_SOURCE,
  WALK_IN_ELIGIBLE_STATUSES,
} from './walk-in-billing-logic';

export * from './walk-in-billing-logic';

/**
 * List eligible walk-in services for a patient. Reactive to invoice_items so a
 * service becomes ineligible the moment it is invoiced. Org scoping is implicit
 * — the local PowerSync DB only contains the current org's rows.
 */
export function qEligibleWalkInServices(patientId: string): QuerySpec<[string]> {
  const statuses = WALK_IN_ELIGIBLE_STATUSES.map((s) => `'${s}'`).join(', ');
  return {
    key: ['walk-in-services', 'eligible', patientId] as const,
    sql: `
      SELECT
        q.id            AS queue_entry_id,
        q.queue_number  AS queue_number,
        q.queue_date    AS queue_date,
        q.status        AS status,
        q.clinic_id     AS clinic_id,
        q.service_id    AS service_id,
        q.provider_id   AS provider_id,
        ap.display_name AS provider_name,
        s.name          AS service_name,
        s.price_cents   AS price_cents,
        (SELECT COUNT(*) FROM invoice_items ii
           WHERE ii.source_type = '${WALK_IN_SOURCE_TYPE}'
             AND ii.source_id = q.id
             AND ii.service_id = q.service_id) AS invoiced_count
      FROM queue_entries q
      LEFT JOIN appointment_services s ON s.id = q.service_id
      LEFT JOIN appointment_providers ap ON ap.id = q.provider_id
      WHERE q.source = '${WALK_IN_QUEUE_SOURCE}'
        AND q.patient_id = ?
        AND q.service_id IS NOT NULL
        AND q.status IN (${statuses})
      ORDER BY q.queue_date DESC, q.position ASC
    `,
    params: [patientId],
    watch: ['queue_entries', 'appointment_services', 'appointment_providers', 'invoice_items'],
  };
}
